<template>
  <b-modal
    v-model="isOpen"
    size="lg"
    centered
    header-class="modal-header-full"
    footer-class="d-none"
    hide-header-close
  >
    <template #title>
      <div class="header-layout">
        <div class="header-left">
          <h2 class="modal-title">
            <strong>KPI-Instanz bearbeiten</strong>
          </h2>
          <p class="modal-description">
            Passen Sie die Filter und Parameter Ihrer KPI-Instanz an
          </p>
        </div>
        <div class="header-actions">
          <b-button variant="outline-secondary" size="sm" @click="isOpen = false">
            {{ $t('Webportal.Dialog.Cancel')}}
          </b-button>
          <b-button variant="primary" size="sm" @click="isOpen = false">
            {{ $t('Webportal.Save')}}
          </b-button>
        </div>
      </div>
    </template>

    <template #default>
      <div class="modal-content">
        <!-- Current KPI Info -->
        <div class="kpi-info-box">
          <p class="kpi-info-name">{{ kpi?.name }}</p>
          <p class="kpi-info-category">{{ kpi?.category }}</p>
        </div>

        <!-- Market/Country Select -->
        <b-form-group
          label="Land / Markt"
          label-for="edit-market"
          class="form-group"
        >
          <b-form-select
            id="edit-market"
            v-model="editForm.market"
            :options="marketOptions"
            class="form-select"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Markt auswählen
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <!-- Wave Select -->
        <b-form-group
          label="Welle"
          label-for="edit-wave"
          class="form-group"
        >
          <b-form-select
            id="edit-wave"
            v-model="editForm.wave"
            :options="waveOptions"
            class="form-select"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Welle auswählen
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <!-- Sub-KPI Select -->
        <b-form-group
          label="Unter-KPI"
          label-for="edit-subkpi"
          class="form-group"
        >
          <b-form-select
            id="edit-subkpi"
            v-model="editForm.subKpi"
            :options="subKpiOptions"
            class="form-select"
          >
            <template #first>
              <b-form-select-option :value="null" disabled>
                Unter-KPI auswählen
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <!-- Preview -->
        <div class="preview-box">
          <p class="preview-title">
            <Grid3x3 :size="12" />
            Vorschau der KPI-Ansichten
          </p>

          <div class="preview-grid">
            <!-- Grid View Preview -->
            <div class="preview-item">
              <p class="preview-label">
                <Grid3x3 :size="12" />
                Grid-Ansicht
              </p>
              <div class="preview-card preview-card-grid">
                <div class="preview-badges">
                  <span class="preview-badge">{{ editForm.market || 'DE' }}</span>
                  <span class="preview-separator">•</span>
                  <span class="preview-badge">{{ editForm.wave || '25T3' }}</span>
                  <span class="preview-separator">•</span>
                  <span class="preview-badge">Datafreeze</span>
                </div>
                <div>
                  <p class="preview-category">{{ kpi?.category }}</p>
                  <p class="preview-name">{{ kpi?.name }} ({{ editForm.subKpi || 'Gesamt' }})</p>
                </div>
                <div class="preview-value-row">
                  <span class="preview-value">{{ kpi?.value }}%</span>
                  <span class="preview-target">/ {{ kpi?.target }}% Ziel</span>
                </div>
                <div class="preview-footer">
                  <span>Vorwelle: {{ kpi?.previousValue }}%</span>
                  <div :class="['preview-indicator', `preview-indicator-${getStatusColor(kpi)}`]" />
                </div>
              </div>
            </div>

            <!-- List View Preview -->
            <div class="preview-item">
              <p class="preview-label">
                <List :size="12" />
                Listen-Ansicht
              </p>
              <div class="preview-card preview-card-list">
                <div class="preview-badges">
                  <span class="preview-badge">{{ editForm.market || 'DE' }}</span>
                  <span class="preview-separator">•</span>
                  <span class="preview-badge">{{ editForm.wave || '25T3' }}</span>
                  <span class="preview-separator">•</span>
                  <span class="preview-badge">{{ editForm.subKpi || 'Overall (Gesamt)' }}</span>
                </div>
                <div class="preview-list-content">
                  <div class="preview-list-left">
                    <p class="preview-list-name">{{ kpi?.category }}</p>
                    <p class="preview-list-previous">Vorwelle: {{ kpi?.previousValue }}%</p>
                  </div>
                  <div class="preview-list-right">
                    <span class="preview-list-value">{{ kpi?.value }}%</span>
                    <span class="preview-list-target">/{{ kpi?.target }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

  </b-modal>
</template>

<script>
import { Grid3x3, List, Save } from 'lucide-vue-next';

export default {
  name: 'ModalEditKpi',

  components: {
    Grid3x3,
    List,
    Save
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    kpi: {
      type: Object,
      default: null
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      editForm: {
        market: null,
        wave: null,
        subKpi: null
      },
      marketOptions: [
        { value: 'DE', text: 'Deutschland' },
        { value: 'AT', text: 'Österreich' },
        { value: 'CH', text: 'Schweiz' },
        { value: 'FR', text: 'Frankreich' },
        { value: 'UK', text: 'Vereinigtes Königreich' }
      ],
      waveOptions: [
        { value: '25T3', text: '25T3 (aktuell) • Datafreeze' },
        { value: '25T2', text: '25T2 • Abgeschlossen' },
        { value: '25T1', text: '25T1 • Abgeschlossen' },
        { value: '24T4', text: '24T4 • Abgeschlossen' }
      ],
      subKpiOptions: [
        { value: 'Overall', text: 'Overall (Gesamt)' },
        { value: 'Personal', text: 'Personal response' },
        { value: 'Experience', text: 'Experience' },
        { value: 'Complete', text: 'Offer sent complete' }
      ]
    };
  },

  computed: {
    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    }
  },

  watch: {
    kpi: {
      immediate: true,
      handler(newKpi) {
        if (newKpi) {
          this.editForm.market = newKpi.country || 'DE';
          this.editForm.wave = newKpi.period || '25T3';
          this.editForm.subKpi = newKpi.subKpi || 'Overall';
        }
      }
    }
  },

  methods: {
    getStatusColor(kpi) {
      if (!kpi?.value || !kpi?.target) return 'gray';

      const ratio = kpi.value / kpi.target;
      if (ratio >= 1) return 'green';
      if (ratio >= 0.95) return 'yellow';
      return 'red';
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: unset;
}

.kpi-info-box {
  padding: 12px;
  background: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: 8px;
}

.kpi-info-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0 0 4px;
}

.kpi-info-category {
  font-size: 12px;
  color: var(--color-muted-foreground);
  margin: 0;
}

.form-group {
  margin-bottom: 0;

  :deep(label) {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
  }
}

.form-select {
  :deep(.form-select) {
    font-size: 14px;
  }
}

.preview-box {
  padding: 12px;
  background: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  margin-top: 8px;
}

.preview-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--bmw-primary);
  margin: 0 0 12px;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.preview-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-muted-foreground);
  margin: 0;
}

.preview-card {
  padding: 8px;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 10px;
}

.preview-card-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preview-badges {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.preview-badge {
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 500;
  background: #f9fafb;
  color: var(--color-muted-foreground);
  border: 1px solid var(--color-border);
  border-radius: 3px;
}

.preview-separator {
  font-size: 10px;
  color: #d1d5db;
}

.preview-category {
  font-size: 10px;
  color: var(--color-muted-foreground);
  margin: 0 0 2px;
}

.preview-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-value-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.preview-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--bmw-primary);
}

.preview-target {
  font-size: 10px;
  color: var(--color-muted-foreground);
}

.preview-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 10px;
  color: var(--color-muted-foreground);
}

.preview-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;

  &-green {
    background: var(--default-status-green);
  }

  &-yellow {
    background: var(--default-status-yellow);
  }

  &-red {
    background: var(--default-status-red);
  }

  &-gray {
    background: var(--default-status-indifferent);
  }
}

.preview-card-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.preview-list-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.preview-list-left {
  flex: 1;
  min-width: 0;
}

.preview-list-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-foreground);
  margin: 0 0 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-list-previous {
  font-size: 10px;
  color: var(--color-muted-foreground);
  margin: 0;
}

.preview-list-right {
  display: flex;
  align-items: baseline;
  gap: 2px;
  flex-shrink: 0;
}

.preview-list-value {
  font-size: 18px;
  font-weight: 700;
  color: var(--bmw-primary);
}

.preview-list-target {
  font-size: 10px;
  color: var(--color-muted-foreground);
}

// Dark Mode
:root.dark,
[data-bs-theme="dark"] {

  .kpi-info-box,
  .preview-box {
    background: var(--color-muted);
    border-color: var(--color-border);
  }

  .kpi-info-name,
  .preview-list-name {
    color: var(--color-foreground);
  }

  .kpi-info-category,
  .preview-category,
  .preview-target,
  .preview-footer,
  .preview-list-previous,
  .preview-list-target {
    color: var(--color-muted-foreground);
  }

  .preview-card {
    background: var(--color-card);
    border-color: var(--color-border);
  }

  .preview-badge {
    background: var(--color-card);
    color: var(--color-foreground);
    border-color: var(--color-border);
  }

  .preview-value,
  .preview-list-value {
    color: var(--color-primary);
  }
}
</style>

